import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { BILLING_CYCLE_STEP_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the package picker's billing-cycle-step arm (`symphonyBillingCycleStepWeb`).
 *
 * `false` is CONTROL — the shipped one-screen picker with the Monthly/Yearly toggle. `true` is the
 * variant: no toggle, and the cycle is chosen on a second screen after the plan. An unreachable route
 * reads as control, matching the server's own fail-closed default (pages/api/flags/billing-cycle-step).
 *
 * Fetched once and cached for the page load. That cache is what keeps the picker on ONE arm: the
 * component asks again on every re-render and on the way back from the cycle screen, and a buyer must
 * not be moved between arms mid-purchase.
 *
 * A dev-tools **Feature toggles** override wins over the conducted value, so forcing it On/Off
 * exercises the real flow rather than a special case. That override is inert without a dev-tools
 * grant, so a real user always gets the conducted value.
 */

/**
 * How long the picker will wait for the arm before serving control.
 *
 * **This bound is load-bearing, not defensive tidiness.** `SymphonyPricing` holds the plan cards
 * until the arm resolves — it has to, because a card rendered before the gate answers falls through
 * to a one-click yearly checkout for a buyer who was never asked the cycle question. But that means
 * an unbounded fetch here parks EVERY visitor, both arms and at any ramp percentage including zero,
 * on "Loading plans…" for as long as the route hangs. That is a purchase page: the cost of the route
 * being slow would be nobody buying anything.
 *
 * The route bounds its own `conduct` call at 3s but leaves `getUserData` unbounded, so it cannot be
 * relied on to answer at all. 1.5s is comfortably longer than a healthy same-origin round trip and
 * short enough that a buyer reads it as the page loading rather than as the page being broken.
 */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function billingCycleStepEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(BILLING_CYCLE_STEP_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/billing-cycle-step`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  return cached;
}

/**
 * Resolve the arm for rendering: `undefined` until the answer lands, then `true`/`false`.
 *
 * The undefined state is load-bearing, not laziness. Starting at `false` would paint the control
 * toggle for a variant user and then snatch it away a moment later — the picker's header would
 * visibly reflow, and a fast clicker could pick a cadence in a UI that is about to stop existing. The
 * caller reserves the toggle's space while this is `undefined` instead (see `SymphonyPricing`).
 */
export function useBillingCycleStep(): boolean | undefined {
  const [arm, setArm] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    let live = true;
    void billingCycleStepEnabled().then((on) => {
      if (live) setArm(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return arm;
}
